import { inject, Injectable } from '@angular/core';
import { AppRoutes } from '@core/routes/route.paths';
import { AppService } from '@core/services/app.service';
import { UserService } from '@core/services/user/user.service';
import { SubMenu } from '@shared/model/menu-item.type';

@Injectable({
  providedIn: 'root',
})
export class ValidatorMenuService {
  // Warning icon shown when an output has pending items.
  private static readonly WARNING_ICON = 'pbi-icon-mini pbi-warning-triangle';

  private readonly appService = inject(AppService);
  private readonly userService = inject(UserService);

  // Super-users (admin/support) see every validator page; other users are gated by their url access rights.
  private canShow(route: string): boolean {
    return this.userService.isSuperUser() || this.userService.checkAccessRights(route);
  }

  getMenu(): SubMenu {
    // param flags feed the warning icon only; visibility is driven by canShow.
    const param = this.appService.validatorMenuParam();

    return {
      root: { label: 'main-menu.validator', routerLink: this.userService.firstAccessibleValidatorRoute() },
      items: [
        {
          label: 'sub-menu-validator.upload',
          routerLink: AppRoutes.Validator.Upload,
          show: () => this.canShow(AppRoutes.Validator.Upload),
        },
        {
          label: 'menu.drop-files',
          routerLink: AppRoutes.Validator.FileDeposit,
          show: () => this.canShow(AppRoutes.Validator.FileDeposit),
        },
        {
          label: 'sub-menu-validator.input',
          routerLink: AppRoutes.Validator.Input,
          show: () => this.canShow(AppRoutes.Validator.Input),
          icon: param?.INPUT === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.email-output',
          routerLink: AppRoutes.Validator.EmailOutput,
          show: () => this.canShow(AppRoutes.Validator.EmailOutput),
          icon: param?.EMAIL === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.mod-output',
          routerLink: AppRoutes.Validator.ModOutput,
          show: () => this.canShow(AppRoutes.Validator.ModOutput),
          icon: param?.MOD === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.chorus-output',
          routerLink: AppRoutes.Validator.ChorusOutput,
          show: () => this.canShow(AppRoutes.Validator.ChorusOutput),
          icon: param?.CHORUS === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.archive-output',
          routerLink: AppRoutes.Validator.ArchiveOutput,
          show: () => this.canShow(AppRoutes.Validator.ArchiveOutput),
          icon: param?.ARCHIVE === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.sms-output',
          routerLink: AppRoutes.Validator.SmsOutput,
          show: () => this.canShow(AppRoutes.Validator.SmsOutput),
          icon: param?.SMS === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.lre-output',
          routerLink: AppRoutes.Validator.LreOutput,
          show: () => this.canShow(AppRoutes.Validator.LreOutput),
          icon: param?.LRE === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.edi-output',
          routerLink: AppRoutes.Validator.EdiOutput,
          show: () => this.canShow(AppRoutes.Validator.EdiOutput),
          icon: param?.EDI === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
        {
          label: 'sub-menu-validator.digital-output',
          routerLink: AppRoutes.Validator.DigitalOutput,
          show: () => this.canShow(AppRoutes.Validator.DigitalOutput),
          icon: param?.DIGITAL === 1 ? ValidatorMenuService.WARNING_ICON : null,
        },
      ],
    };
  }
}
